import React, { useMemo, useState } from "react";
import { Moon, Sun } from "lucide-react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from "recharts";
import { Home, BarChart3, Layers } from "lucide-react";
import "./App.css";


const districts = [
  "Anantapur","Chittoor","East Godavari","Guntur","Krishna","Kurnool",
  "Nellore","Prakasam","Srikakulam","Visakhapatnam","Vizianagaram",
  "West Godavari","Palnadu","Nandyal","Tirupati","Eluru","Bapatla",
  "Nellore Rural","NTR","YSR-Kadapa","Annamayya","AlluriSitaramaRaju",
  "Kakinada","Konaseema","Parvathipuram","SriPottiSriramuluNellore"
];

const districtData = districts.map((name, idx) => {
  const base = 800 + idx * 60;
  const resolved = Math.round(base * (0.72 + (idx % 5) * 0.03));
  const pending = Math.round(base * (0.18 + (idx % 4) * 0.02));
  const escalated = Math.round(base * (0.05 + (idx % 3) * 0.015));
  const total = resolved + pending + escalated;
  return { name, total, resolved, pending, escalated, resolutionRate: +(resolved/total*100).toFixed(1) };
});

const departments = [
  "Revenue", "Health", "Education", "Transport", "Police",
  "Panchayat", "Water", "Energy", "Agriculture", "Urban Development",
  "Women & Child", "Labour", "Housing", "Environment", "IT",
  "Food & Civil Supplies", "Legal", "Social Welfare"
];

const deptData = departments.map((d, i) => {
  const base = 500 + i * 50;
  const resolved = Math.round(base * (0.68 + (i % 4) * 0.04));
  const pending = Math.round(base * (0.22 + (i % 3) * 0.02));
  const escalated = Math.round(base * (0.06 + (i % 5) * 0.01));
  const total = resolved + pending + escalated;
  const monthly = ["Jan","Feb","Mar","Apr","May","Jun"].map((m, idx) => {
    const variation = Math.round(base * (0.8 + Math.sin((i+1)*(idx+1))*0.12));
    return { month: m, value: variation };
  });
  return { name: d, total, resolved, pending, escalated, resolutionRate: +(resolved/total*100).toFixed(1), monthly };
});

const COLORS = ["#00c853","#2962ff","#ff6d00","#d50000","#00bfa5"];

function aggregateMonthly(allDeptData) {
  const months = ["Jan","Feb","Mar","Apr","May","Jun"];
  return months.map((m, idx) => {
    const value = allDeptData.reduce((sum, dept) => sum + dept.monthly[idx].value, 0);
    return { month: m, value };
  });
}


export default function App() {
  const [darkMode, setDarkMode] = useState(false);

  const [selectedDistrict, setSelectedDistrict] = useState("All Districts");
  const [selectedDepartment, setSelectedDepartment] = useState("All Departments");
  const [role, setRole] = useState("CM"); // CM | SECRETARY | COLLECTOR | OFFICER
const isCM = role === "CM";
const isSecretary = role === "SECRETARY";
const isCollector = role === "COLLECTOR";
const isOfficer = role === "OFFICER";


  const districtChartData = useMemo(() => {
    if (selectedDistrict === "All Districts") {
      return districtData.map(d => ({ name: d.name, total: d.total, resolved: d.resolved }));
    }
    const d = districtData.find(x => x.name === selectedDistrict);
    return d ? [{ name: d.name, total: d.total, resolved: d.resolved }] : [];
  }, [selectedDistrict]);

  const departmentChartData = useMemo(() => {
    if (selectedDepartment === "All Departments") {
      return deptData.map(d => ({ name: d.name, total: d.total, resolved: d.resolved }));
    }
    const d = deptData.find(x => x.name === selectedDepartment);
    return d ? [{ name: d.name, total: d.total, resolved: d.resolved }] : [];
  }, [selectedDepartment]);

  const pieData = useMemo(() => {
    if (selectedDepartment === "All Departments") {
      const totals = deptData.reduce((acc, d) => {
        acc.resolved += d.resolved;
        acc.pending += d.pending;
        acc.escalated += d.escalated;
        return acc;
      }, { resolved:0, pending:0, escalated:0 });
      return [
        { name: "Resolved", value: totals.resolved },
        { name: "Pending", value: totals.pending },
        { name: "Escalated", value: totals.escalated },
      ];
    } else {
      const d = deptData.find(x => x.name === selectedDepartment);
      return d ? [
        { name: "Resolved", value: d.resolved },
        { name: "Pending", value: d.pending },
        { name: "Escalated", value: d.escalated },
      ] : [];
    }
  }, [selectedDepartment]);

  const monthlyTrend = useMemo(() => {
    if (selectedDepartment === "All Departments") {
      return aggregateMonthly(deptData);
    } else {
      const d = deptData.find(x => x.name === selectedDepartment);
      return d ? d.monthly : [];
    }
  }, [selectedDepartment]);

  const districtRankings = useMemo(() => {
    const sorted = [...districtData].sort((a,b) => b.resolutionRate - a.resolutionRate);
    return sorted.slice(0,8); // top 8
  }, []);

  const departmentRankings = useMemo(() => {
    const sorted = [...deptData].sort((a,b) => b.resolutionRate - a.resolutionRate);
    return sorted.slice(0,8);
  }, []);

  return (
    <div className={`main-container ${darkMode ? "dark" : ""}`}>

<header className="cm-header">
  <img src="/apcm3.jpg" alt="CM" className="cm-photo" />

  <div className="cm-title">
    <h1>Government of Andhra Pradesh</h1>
    <h2>Chief Minister’s Governance Dashboard</h2>
    <p>Citizen Grievance Monitoring System</p>
  </div>

  <img src="/aplogo.png" alt="AP Logo" className="ap-logo-large" />

  <button className="dark-toggle" onClick={() => setDarkMode(!darkMode)}>
    {darkMode ? <Sun size={18}/> : <Moon size={18}/>}
  </button>
</header>

      <aside className="sidebar">
        <div className="sidebar-top">
          <img src="/aplogo.png" alt="AP Logo" className="ap-logo-small"/>
          <h2>AP CM Dashboard</h2>
        </div>
        <nav className="nav">
          <button className="nav-btn active"><Home size={16}/> Overview</button>
          <button className="nav-btn"><BarChart3 size={16}/> Districts</button>
          <button className="nav-btn"><Layers size={16}/> Departments</button>
        </nav>
      </aside>

      <main className="content">
        <header className="header">
          <h1>Chief Minister’s Governance Dashboard</h1>
          <p className="subtitle">Real-time Citizen Grievance Monitoring System</p>
          {/* ROLE SELECTOR */}
<div className="role-select">
  <label>
    Role:
    <select value={role} onChange={(e) => setRole(e.target.value)}>
      <option value="CM">Chief Minister</option>
      <option value="SECRETARY">Secretary</option>
      <option value="COLLECTOR">District Collector</option>
      <option value="OFFICER">Department Officer</option>
    </select>
  </label>
</div>


          <div className="filters">
            {(isCM || isSecretary) && (
  <label>
    District:
    <select value={selectedDistrict} onChange={e=>setSelectedDistrict(e.target.value)}>
      <option>All Districts</option>
      {districts.map((d) => (
        <option key={d} value={d}>{d}</option>
      ))}
    </select>
  </label>
)}


            {!isCollector && (
  <label>
    Department:
    <select value={selectedDepartment} onChange={e=>setSelectedDepartment(e.target.value)}>
      <option>All Departments</option>
      {departments.map((d) => (
        <option key={d} value={d}>{d}</option>
      ))}
    </select>
  </label>
)}

          </div>
        </header>

        <section className="overview">
          <div className="stats">
            <div className="stat-card">
              <div className="stat-title">Total Complaints (All Depts)</div>
              <div className="stat-value">{deptData.reduce((s,d)=>s+d.total,0).toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-title">Total Resolved</div>
              <div className="stat-value">{deptData.reduce((s,d)=>s+d.resolved,0).toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-title">Overall Resolution %</div>
              <div className="stat-value">
                {Math.round((deptData.reduce((s,d)=>s+d.resolved,0)/deptData.reduce((s,d)=>s+d.total,0))*100)}%
              </div>
            </div>
          </div>

          <div className="charts-grid">
            <div className="chart-card">
              <h3>District Complaints</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={districtChartData} margin={{ top: 8, right: 16, left: 0, bottom: 6 }}>
                  <XAxis dataKey="name" tick={{fontSize:12}} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="total" fill="#e4530aff" name="Total"/>
                  <Bar dataKey="resolved" fill="#00c853" name="Resolved" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3>Department Complaints</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={departmentChartData} margin={{ top: 8, right: 16, left: 0, bottom: 6 }}>
                  <XAxis dataKey="name" tick={{fontSize:12}} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="total" fill="#e4530aff" name="Total"/>
                  <Bar dataKey="resolved" fill="#00c853" name="Resolved"/>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3>{selectedDepartment === "All Departments" ? "Departments: Status (Aggregate)" : `${selectedDepartment} — Status`}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                    {pieData.map((entry, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card-wide">
              <h3>{selectedDepartment === "All Departments" ? "Monthly Complaints (All Departments)" : `${selectedDepartment} — Monthly Trend`}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={monthlyTrend}>
                  <CartesianGrid stroke="#eee" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="value" stroke="#2962ff" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

{!isOfficer && (
  <div className="rankings">
            <section className="heat-section">
  <h3>District Performance Heat Ranking</h3>

  <ul className="heat-list">
    {districtRankings.map(d => (
      <li
        key={d.name}
        className={
          d.resolutionRate > 80 ? "heat high" :
          d.resolutionRate > 65 ? "heat mid" : "heat low"
        }
      >
        <span>{d.name}</span>
        <b>{d.resolutionRate}%</b>
      </li>
    ))}
  </ul>
</section>

            <div className="rank-box">
              <h4>Top Districts (by Resolution %)</h4>
              <ol>
                {districtRankings.map(d => (
                  <li key={d.name}><b>{d.name}</b> — {d.resolutionRate}%</li>
                ))}
              </ol>
            </div>

            <div className="rank-box">
              <h4>Top Departments (by Resolution %)</h4>
              <ol>
                {departmentRankings.map(d => (
                  <li key={d.name}><b>{d.name}</b> — {d.resolutionRate}%</li>
                ))}
              </ol>
            </div>
          </div>
          )}

        </section>
      </main>
    </div>
  );
}
